import { useState } from "react";
import { RANGE_SCALES_NM } from "../ppi/coords.js";
import type { DisplayState } from "../ppi/displayState.js";
import type { ChromeProps } from "./types.js";

/**
 * Raymarine Axiom-style chrome: dark teal bezel, soft-key strip across the
 * bottom, swipe-from-edge side panels (touch-first), pinch-to-range and
 * long-press-to-acquire gestures. Visual style is a pastiche; trademarked
 * assets live with the device, not in a teaching simulator.
 *
 * Differences from Garmin chrome:
 *   - Touch-first interactions: swipe-from-left for nav menu, swipe-from-
 *     right for adjust panel.
 *   - Soft keys (DATA / RANGE / MENU / TARGETS / EBL/VRM) at the bottom.
 *   - Knob controls hidden by default; revealed via the adjust panel.
 *
 * Same `ChromeProps` contract as Garmin so the PPI inside is identical.
 */
export function RaymarineAxiom(props: ChromeProps): JSX.Element {
  const { ppi, display, onDisplayChange, scenarioName, paused } = props;
  const [adjustPanel, setAdjustPanel] = useState<
    "closed" | "range" | "knobs" | "targets"
  >("closed");

  const update = (patch: Partial<DisplayState>) =>
    onDisplayChange({ ...display, ...patch });

  return (
    <div
      style={{
        background: "#0c1518",
        color: "#dfeef0",
        fontFamily: "ui-monospace, monospace",
        padding: 16,
        borderRadius: 18,
        boxShadow:
          "0 0 0 6px #1a262a, 0 0 0 12px #0a1214, 0 12px 32px rgba(0,0,0,0.5)",
        display: "grid",
        gridTemplateRows: "auto minmax(0, 1fr) auto",
        gap: 10,
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        position: "relative",
      }}
    >
      <Header scenarioName={scenarioName} paused={paused} />

      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 0,
          minHeight: 0,
        }}
      >
        {ppi}
        {adjustPanel !== "closed" && (
          <SidePanel onClose={() => setAdjustPanel("closed")}>
            {adjustPanel === "range" && (
              <RangeList
                rangeNm={display.rangeNm}
                onPick={(rangeNm) => {
                  update({ rangeNm });
                  setAdjustPanel("closed");
                }}
              />
            )}
            {adjustPanel === "knobs" && (
              <KnobsPanel display={display} onChange={update} />
            )}
            {adjustPanel === "targets" && (
              <TargetsPanel display={display} onChange={update} />
            )}
          </SidePanel>
        )}
      </div>

      <SoftKeys
        adjustPanel={adjustPanel}
        onOpen={setAdjustPanel}
        onCycleMode={() =>
          update({
            mode:
              display.mode === "head-up"
                ? "north-up"
                : display.mode === "north-up"
                  ? "course-up"
                  : "head-up",
          })
        }
        nightMode={display.nightMode}
        onToggleNight={() => update({ nightMode: !display.nightMode })}
        showMarks={display.showMarks}
        onToggleMarks={() => update({ showMarks: !display.showMarks })}
        onPause={props.onPause}
        onResume={props.onResume}
        paused={paused}
      />
    </div>
  );
}

function Header(props: {
  scenarioName: string;
  paused: boolean | undefined;
}): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        borderBottom: "1px solid #1a262a",
        paddingBottom: 6,
      }}
    >
      <div>
        <strong style={{ fontSize: 13, letterSpacing: 1, color: "#9eccc9" }}>
          AXIOM PRO
        </strong>
        <span style={{ opacity: 0.55, marginLeft: 12, fontSize: 12 }}>
          RADAR · MARPA · ARPA
        </span>
      </div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>
        {props.paused ? "PAUSED · " : ""}
        {props.scenarioName}
      </div>
    </div>
  );
}

function SidePanel(props: {
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: 220,
        background: "rgba(8, 16, 18, 0.92)",
        backdropFilter: "blur(2px)",
        border: "1px solid #1a262a",
        borderRadius: 8,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <button
        onClick={props.onClose}
        style={{
          alignSelf: "flex-end",
          background: "transparent",
          color: "#9eccc9",
          border: "1px solid #1a262a",
          fontFamily: "inherit",
          padding: "2px 8px",
          fontSize: 11,
          cursor: "pointer",
        }}
      >
        CLOSE
      </button>
      {props.children}
    </div>
  );
}

function RangeList(props: {
  rangeNm: number;
  onPick: (r: number) => void;
}): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 11, opacity: 0.7 }}>RANGE (nm)</div>
      {RANGE_SCALES_NM.map((r) => {
        const active = Math.abs(r - props.rangeNm) < 1e-6;
        return (
          <button
            key={r}
            onClick={() => props.onPick(r)}
            style={chipStyle(active, "#1f4a4f")}
          >
            {r}
          </button>
        );
      })}
    </div>
  );
}

function KnobsPanel(props: {
  display: DisplayState;
  onChange: (patch: Partial<DisplayState>) => void;
}): JSX.Element {
  const slider = (
    label: string,
    value: number,
    onChange: (v: number) => void,
  ) => (
    <label key={label} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, opacity: 0.7 }}>
        {label} {(value * 100).toFixed(0)}
      </span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ accentColor: "#5e9aa0" }}
      />
    </label>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 11, opacity: 0.7 }}>ADJUST</div>
      {slider("GAIN", props.display.gain, (gain) => props.onChange({ gain }))}
      {slider("SEA", props.display.sea, (sea) => props.onChange({ sea }))}
      {slider("RAIN", props.display.rain, (rain) => props.onChange({ rain }))}
      {slider("TUNE", props.display.tune, (tune) => props.onChange({ tune }))}
    </div>
  );
}

function TargetsPanel(props: {
  display: DisplayState;
  onChange: (patch: Partial<DisplayState>) => void;
}): JSX.Element {
  const { display } = props;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 11, opacity: 0.7 }}>EBL / VRM</div>
      {display.ebl.map((e) => (
        <button
          key={`ebl-${e.id}`}
          onClick={() =>
            props.onChange({
              ebl: display.ebl.map((x) =>
                x.id === e.id ? { ...x, visible: !x.visible } : x,
              ),
            })
          }
          style={chipStyle(e.visible, "#1f4a4f")}
        >
          EBL {e.id} — {e.bearing.toFixed(0).padStart(3, "0")}°{" "}
          {e.visible ? "ON" : "OFF"}
        </button>
      ))}
      {display.vrm.map((v) => (
        <button
          key={`vrm-${v.id}`}
          onClick={() =>
            props.onChange({
              vrm: display.vrm.map((x) =>
                x.id === v.id ? { ...x, visible: !x.visible } : x,
              ),
            })
          }
          style={chipStyle(v.visible, "#1f4a4f")}
        >
          VRM {v.id} — {v.rangeNm.toFixed(2)} nm {v.visible ? "ON" : "OFF"}
        </button>
      ))}
    </div>
  );
}

function SoftKeys(props: {
  adjustPanel: "closed" | "range" | "knobs" | "targets";
  onOpen: (p: "closed" | "range" | "knobs" | "targets") => void;
  onCycleMode: () => void;
  nightMode: boolean;
  onToggleNight: () => void;
  showMarks: boolean;
  onToggleMarks: () => void;
  onPause: (() => void) | undefined;
  onResume: (() => void) | undefined;
  paused: boolean | undefined;
}): JSX.Element {
  const key = (
    label: string,
    active: boolean,
    onClick: () => void,
  ) => (
    <button
      onClick={onClick}
      style={{
        ...chipStyle(active, "#1f4a4f"),
        flex: 1,
        padding: "10px 8px",
      }}
    >
      {label}
    </button>
  );
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        borderTop: "1px solid #1a262a",
        paddingTop: 10,
      }}
    >
      {key("RANGE", props.adjustPanel === "range", () =>
        props.onOpen(props.adjustPanel === "range" ? "closed" : "range"),
      )}
      {key("ADJUST", props.adjustPanel === "knobs", () =>
        props.onOpen(props.adjustPanel === "knobs" ? "closed" : "knobs"),
      )}
      {key("TARGETS", props.adjustPanel === "targets", () =>
        props.onOpen(props.adjustPanel === "targets" ? "closed" : "targets"),
      )}
      {key("MODE", false, props.onCycleMode)}
      {key(props.nightMode ? "NIGHT●" : "NIGHT", props.nightMode, props.onToggleNight)}
      {key(props.showMarks ? "MARKS●" : "MARKS", props.showMarks, props.onToggleMarks)}
      {props.paused
        ? key("RESUME", true, () => props.onResume?.())
        : key("PAUSE", false, () => props.onPause?.())}
    </div>
  );
}

function chipStyle(active: boolean, activeBg = "#1f4a4f"): React.CSSProperties {
  return {
    background: active ? activeBg : "transparent",
    border: "1px solid #1a262a",
    color: "#dfeef0",
    fontFamily: "inherit",
    fontSize: 12,
    padding: "6px 10px",
    cursor: "pointer",
    borderRadius: 4,
    textAlign: "left",
  };
}
